import { useEffect, useState, useCallback } from 'react';
import {
  doc,
  onSnapshot,
  getCountFromServer,
  collection,
  updateDoc
} from 'firebase/firestore';
import db from '@services/firebase-service';

const useStreamParticipantsStatus = (streamId) => {
  const [streamInfo, setStreamInfo] = useState({
    hasReachedMaxParticipants: false,
    noOfParticipants: 0
  });

  const [linkLibraryCount, setLinkLibraryCount] = useState(0);
  const [numOfParticipants, setNumOfParticipants] = useState(0);
  const [quickQuestionsCount, setQuickQuestionsCount] = useState(0);

  const [commentCountPerFeature, setCommentCountPerFeature] = useState({});

  const [linkLibraryLastViewedCount, setLinkLibraryLastViewedCount] = useState(0);
  const [quickQuestionsLastViewedCount, setQuickQuestionsLastViewedCount] = useState(0);

  useEffect(() => {
    if (!streamId) return;

    const unsubStreamInfo = onSnapshot(doc(db, 'streams', streamId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setStreamInfo({
          hasReachedMaxParticipants: data?.hasReachedMaxParticipants || false,
          noOfParticipants: data?.noOfParticipants || 0
        });
        setNumOfParticipants(data?.noOfParticipants || 0);
        setCommentCountPerFeature(data?.commentCountPerFeature || {});
      }
    });

    // Subscribe to linkLibrary collection changes
    const linkLibraryRef = collection(db, 'streams', streamId, 'linkLibrary');
    const unsubLinkLibrary = onSnapshot(linkLibraryRef, async () => {
      const snapshot = await getCountFromServer(linkLibraryRef);
      const currentCount = snapshot.data().count;
      const newCount = Math.max(0, currentCount - linkLibraryLastViewedCount);
      setLinkLibraryCount(newCount);
    });

    // Subscribe to quickQuestions collection changes
    const quickQuestionsRef = collection(db, 'streams', streamId, 'quickQuestions');
    const unsubQuickQuestions = onSnapshot(quickQuestionsRef, async () => {
      const snapshot = await getCountFromServer(quickQuestionsRef);
      const currentCount = snapshot.data().count;
      const newCount = Math.max(0, currentCount - quickQuestionsLastViewedCount);
      setQuickQuestionsCount(newCount);
    });

    return () => {
      unsubStreamInfo();
      unsubLinkLibrary();
      unsubQuickQuestions();
    };
  }, [streamId, linkLibraryLastViewedCount, quickQuestionsLastViewedCount]);

  const markLinkLibraryAsViewed = useCallback(async () => {
    const linkLibraryRef = collection(db, 'streams', streamId, 'linkLibrary');
    const snapshot = await getCountFromServer(linkLibraryRef);
    setLinkLibraryLastViewedCount(snapshot.data().count);
    setLinkLibraryCount(0);
  }, [streamId]);

  const markQuickQuestionsAsViewed = useCallback(async () => {
    const quickQuestionsRef = collection(db, 'streams', streamId, 'quickQuestions');
    const snapshot = await getCountFromServer(quickQuestionsRef);
    setQuickQuestionsLastViewedCount(snapshot.data().count);
    setQuickQuestionsCount(0);
  }, [streamId]);

  const markNumOfParticipantsAsViewed = useCallback(async () => {
    const streamRef = doc(db, 'streams', streamId);
    await updateDoc(streamRef, { noOfParticipants: numOfParticipants });
    setNumOfParticipants(0);
  }, [streamId, numOfParticipants]);

  return {
    ...streamInfo,
    commentCountPerFeature,
    linkLibraryCount,
    numOfParticipants,
    quickQuestionsCount,
    markLinkLibraryAsViewed,
    markQuickQuestionsAsViewed,
    markNumOfParticipantsAsViewed
  };
};

export default useStreamParticipantsStatus;
